import React, { useState, useEffect, useRef } from 'react';
import { oratricslogo } from '../Assets';
// No longer need format, parseISO from date-fns for displayDate/Time
// import { format, parseISO } from 'date-fns';
import { TextField } from '@mui/material';
import baseUrl from '../config';

const InvitationPage = () => {
  // --- UI State ---
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [showPlatformDetails, setShowPlatformDetails] = useState(false);
  const [showGame, setShowGame] = useState(false);

  // --- Game State ---
  const [score, setScore] = useState(0);
  const [gameActive, setGameActive] = useState(false);
  const [starPosition, setStarPosition] = useState({ x: 0, y: 0 });
  const gameAreaRef = useRef(null);

  // --- Invitation Link & User Management State ---
  const [invitationCode, setInvitationCode] = useState(null);
  const [eventDetails, setEventDetails] = useState(null); // Now includes displayDate, displayStartTime, etc.
  const [inviterDetails, setInviterDetails] = useState(null);
  const [invitationStatus, setInvitationStatus] = useState('loading'); // 'loading', 'pending', 'accepted', 'expired', 'invalid', 'error', 'no_code'
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [acceptMessage, setAcceptMessage] = useState('');
  const [acceptSeverity, setAcceptSeverity] = useState(''); // 'success', 'error', 'info'

  // New user registration fields
  const [username, setUsername] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userPassword, setUserPassword] = useState('');
  const [isNewUserMode, setIsNewUserMode] = useState(false);

  // Welcome message (can be dynamic based on invitation or user)
  const [welcomeMessage, setWelcomeMessage] = useState('Welcome!');

  // --- Countdown State (if still needed) ---
  const [countdownText, setCountdownText] = useState('');

  // --- Dashboard Challenge State ---
  const [aiQuestion, setAiQuestion] = useState('Click below to get a thought-provoking question!');
  const [challengeAnswer, setChallengeAnswer] = useState('');
  const [challengeFeedback, setChallengeFeedback] = useState('');

  // --- Modal Functions ---
  const showModal = (message) => {
    setModalMessage(message);
    setModalVisible(true);
  };

  const hideModal = () => {
    setModalVisible(false);
    setModalMessage('');
  };

  // --- Invitation Code Validation Logic ---
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');

    if (code) {
      setInvitationCode(code);
      const validateInvitation = async () => {
        setInvitationStatus('loading');
        try {
          const response = await fetch(`${baseUrl}api/crm/validate?code=${code}`);
          const data = await response.json();
          console.log(data, "data from validate invitation");

          if (response.ok) {
            setEventDetails(data.event);
            setInviterDetails(data.inviter);
            setInvitationStatus(data.invitationStatus === 'pending' ? 'pending' : 'accepted'); // Explicitly set to 'pending' or 'accepted'
            setWelcomeMessage(`You're invited to: ${data.event.title}!`);
            setAcceptMessage(data.message || ''); // Set message for already accepted
            setAcceptSeverity(data.message.includes('already accepted') ? 'info' : ''); // Set severity for info
          } else {
            // Handle specific error messages from backend
            if (data.message.includes('expired')) {
              setInvitationStatus('expired');
            } else if (data.message.includes('already accepted')) {
              setInvitationStatus('accepted'); // Set status to accepted
              setEventDetails(data.event); // Make sure event details are still available
            } else if (data.message.includes('cancelled')) {
                setInvitationStatus('cancelled');
            } else {
              setInvitationStatus('invalid');
            }
            setAcceptMessage(data.message || 'Invitation is invalid or an error occurred.');
            setAcceptSeverity('error');
          }
        } catch (error) {
          console.error('Error validating invitation:', error);
          setInvitationStatus('error');
          setAcceptMessage('Failed to validate invitation. Please check your connection.');
          setAcceptSeverity('error');
        }
      };
      validateInvitation();
    } else {
      setInvitationStatus('no_code');
      const userName = urlParams.get('name');
      if (userName) {
        setWelcomeMessage(`Welcome, ${decodeURIComponent(userName)}!`);
      } else {
        setWelcomeMessage('Welcome!');
      }
    }
  }, []);

  // --- Accept Invitation Logic ---
  const handleAcceptInvitation = async () => {
    if (!invitationCode || !eventDetails) {
      setAcceptMessage('Error: Missing invitation details.');
      setAcceptSeverity('error');
      return;
    }

    setAcceptLoading(true);
    setAcceptMessage('');
    setAcceptSeverity('');

    let requestBody = {};
    if (isNewUserMode) {
      if (!username || !userEmail || !userPassword) {
        setAcceptMessage('Please fill in all registration fields (Username, Email, Password).');
        setAcceptSeverity('error');
        setAcceptLoading(false);
        return;
      }
      requestBody = { username, email: userEmail, password: userPassword, isNewUser: true };
    } else {
      requestBody = { isNewUser: false };
    }

    try {
      const authToken = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (authToken && !isNewUserMode) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const response = await fetch(`${baseUrl}api/crm/invitations/accept/${invitationCode}`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();
      console.log(data, "data from accept invitation");

      if (response.ok) {
        setInvitationStatus('accepted');
        setAcceptMessage(data.message || 'Invitation accepted successfully!');
        setAcceptSeverity('success');
        showModal(data.message || `Welcome to ${data.event.title}!`); // Use data.event for updated details
        setEventDetails(data.event); // Update event details with localized times

        if (isNewUserMode && data.token) {
            localStorage.setItem('token', data.token);
            // Consider redirecting new user to dashboard or login
        }

      } else {
        setAcceptMessage(data.message || 'Failed to accept invitation.');
        setAcceptSeverity('error');
      }
    } catch (error) {
      console.error('Error accepting invitation:', error);
      setAcceptMessage('Failed to accept invitation. Please try again.');
      setAcceptSeverity('error');
    } finally {
      setAcceptLoading(false);
    }
  };

  // --- Platform Details Toggle Logic ---
  const togglePlatformDetails = () => {
    setShowPlatformDetails(!showPlatformDetails);
  };

  // --- Game Logic ---
  const startGame = () => {
    setScore(0);
    setGameActive(true);
    moveStar();
  };

  const handleStarClick = () => {
    if (gameActive) {
      setScore(prevScore => prevScore + 1);
      moveStar();
    }
  };

  const moveStar = () => {
    if (gameAreaRef.current) {
      const gameArea = gameAreaRef.current;
      const starSize = 50;
      const maxX = gameArea.clientWidth - starSize;
      const maxY = gameArea.clientHeight - starSize;
      const newX = Math.random() * maxX;
      const newY = Math.random() * maxY;
      setStarPosition({ x: newX < 0 ? 0 : newX, y: newY < 0 ? 0 : newY });
    }
  };

  useEffect(() => {
    let interval;
    if (gameActive) {
      interval = setInterval(moveStar, 1500);
    }
    return () => clearInterval(interval);
  }, [gameActive]);

  // --- Countdown Timer Logic (Kept for general page if needed) ---
  const getNextActivityDate = () => {
    const now = new Date();
    const today = now.getDay();
    let daysUntilFriday = 5 - today;

    if (daysUntilFriday < 0 || (daysUntilFriday === 0 && (now.getHours() > 19 || (now.getHours() === 19 && now.getMinutes() > 0)))) {
      daysUntilFriday += 7;
    }

    const nextActivityDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + daysUntilFriday);
    nextActivityDate.setHours(19, 0, 0, 0);
    return nextActivityDate;
  };


  useEffect(() => {
    const updateCountdown = () => {
      const targetDate = getNextActivityDate();
      const now = new Date().getTime();
      const distance = targetDate.getTime() - now;

      if (distance < 0) {
        setCountdownText("Class is Live Now!");
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      setCountdownText(`Next Class in: ${days}d ${hours}h ${minutes}m ${seconds}s`);
    };

    updateCountdown();
    const countdownInterval = setInterval(updateCountdown, 1000);
    return () => clearInterval(countdownInterval);
  }, []);

  // --- FAQ Toggle Logic ---
  const toggleFaq = (event) => {
    const questionElement = event.currentTarget;
    const answer = questionElement.nextElementSibling;

    document.querySelectorAll('.faq-answer.show').forEach(openAnswer => {
        if (openAnswer !== answer) {
            openAnswer.classList.remove('show');
            openAnswer.style.maxHeight = null;
            openAnswer.previousElementSibling.classList.remove('active');
        }
    });

    if (answer.classList.contains('show')) {
        answer.classList.remove('show');
        questionElement.classList.remove('active');
        answer.style.maxHeight = null;
    } else {
        answer.classList.add('show');
        questionElement.classList.add('active');
        answer.style.maxHeight = answer.scrollHeight + "px";
    }
  };

  // --- Temporary Dashboard Challenge Logic ---
  const handleSubmitChallenge = () => {
    const userAnswer = challengeAnswer.trim().toLowerCase();
    if (userAnswer === 'needle') {
      setChallengeFeedback("Correct! You got it!");
    } else {
      setChallengeFeedback("Hmm, not quite. Think about common objects!");
    }
  };

  // --- AI-Powered Question Logic ---
  const generateAIQuestion = async () => {
    setAiQuestion("Generating a new question...");
    try {
        let chatHistory = [];
        chatHistory.push({ role: "user", parts: [{ text: "Generate a short, engaging, and thought-provoking question for students about learning, technology, or creativity. Make it suitable for a K-12 audience. Respond with only the question." }] });
        const payload = { contents: chatHistory };
        const apiKey = ""; // Canvas will provide
        const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;

        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });

        const result = await response.json();
        if (result.candidates && result.candidates.length > 0 &&
            result.candidates[0].content && result.candidates[0].content.parts &&
            result.candidates[0].content.parts.length > 0) {
            const text = result.candidates[0].content.parts[0].text;
            setAiQuestion(text);
        } else {
            setAiQuestion("Failed to generate question. Please try again.");
            console.error("Unexpected API response structure:", result);
        }
    } catch (error) {
        setAiQuestion("Error generating question. Please check your connection.");
        console.error("Error calling Gemini API:", error);
    }
  };

  // --- Book Demo Button Logic ---
  const handleBookDemo = () => {
    showModal('Fantastic! Our team will contact you shortly to schedule your free demo class. Get ready to learn!');
  };

  // --- Unlock Full Dashboard Button Logic ---
  const handleUnlockDashboard = () => {
    showModal('Ready to unlock your full potential? Sign up for Oratrics Learning Solution to access your complete personalized dashboard and much more!');
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 text-gray-800 antialiased">
      {/* Custom Modal */}
      {modalVisible && (
        <div className="modal-overlay show">
          <div className="modal-content">
            <p id="modal-message" className="text-lg mb-4">{modalMessage}</p>
            <button onClick={hideModal} className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded-full transition duration-300">OK</button>
          </div>
        </div>
      )}

      {/* Header Section */}
   <header className="py-5 px-6 md:px-10 flex justify-between items-center  shadow-xl rounded-b-2xl mx-4 md:mx-8 animate-fade-in-down">
    {/* Logo Area */}
    <div className="flex items-center">
        <img src={oratricslogo} alt="Oratrics Logo" style={{ height: '60px' }} className="drop-shadow-md" />
        {/* Optional: Add a subtle text next to the logo for brand emphasis */}
        {/* <span className="ml-3 text-2xl font-bold text-white tracking-wide">Oratrics</span> */}
    </div>

    {/* Navigation */}
    <nav>
        <ul className="flex space-x-8"> {/* Increased space between links */}
            <li>
                <a href="https://oratrics.com" className="text-black-300 hover:text-black-400 transition duration-300 text-lg font-medium relative group">
                    Platform
                    <span className="absolute left-0 bottom-0 w-0 h-0.5 bg-blue-400 transition-all duration-300 group-hover:w-full"></span>
                </a>
            </li>
            <li>
                <a href="https://oratrics.com" className="text-black-300 hover:text-blue-400 transition duration-300 text-lg font-medium relative group">
                    Courses
                    <span className="absolute left-0 bottom-0 w-0 h-0.5 bg-blue-400 transition-all duration-300 group-hover:w-full"></span>
                </a>
            </li>
            <li>
                <a href="https://demo.oratrics.com" className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-full shadow-lg transition duration-300 transform hover:scale-105 flex items-center">
                    <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                    Book a Demo
                </a>
            </li>
        </ul>
    </nav>

    {/* Custom CSS for animation */}
    <style jsx>{`
        @keyframes fadeInDown {
            from { opacity: 0; transform: translateY(-30px); }
            to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-down { animation: fadeInDown 0.6s ease-out forwards; }
    `}</style>
</header>

      {/* Hero Section - Invitation */}
  <section className="relative text-center py-20 px-4 md:px-8 bg-gradient-to-br from-blue-700 to-indigo-800 overflow-hidden rounded-2xl mx-4 md:mx-8 mt-8 shadow-2xl">
    {/* Subtle Background Overlay for texture */}
    <div className="absolute inset-0 bg-pattern-dots opacity-10 z-0"></div>

    {/* Main Content Area */}
    <div className="relative z-10 max-w-5xl mx-auto">
        <p className="text-xl md:text-2xl font-semibold mb-3 text-blue-300 animate-fade-in">
            {welcomeMessage}
        </p>
        <h2 className="text-5xl md:text-7xl font-extrabold leading-tight mb-8 text-white animate-scale-in drop-shadow-md">
            You're Invited!
        </h2>
        <p className="text-lg md:text-xl mb-12 max-w-4xl mx-auto text-blue-100 leading-relaxed animate-fade-in delay-200">
            Your friend wants you to experience the future of learning with Oratrics Learning Solution's exciting Open Activity Classes. Dive into a world of discovery, creativity, and fun!
        </p>

        {/* Conditional rendering for invitation status */}
        {invitationCode && invitationStatus === 'loading' && (
            <div className="bg-blue-800 bg-opacity-70 text-blue-200 p-6 rounded-xl shadow-xl flex items-center justify-center max-w-md mx-auto animate-pulse-fade">
                <svg className="animate-spin -ml-1 mr-3 h-8 w-8 text-blue-200" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-xl font-medium">Loading invitation details...</span>
            </div>
        )}

        {/* Show event details and accept form if invitation is pending */}
        {(invitationStatus === 'pending' || invitationStatus === 'valid') && eventDetails && (
            <div className="bg-white bg-opacity-15 backdrop-filter backdrop-blur-lg p-8 rounded-2xl shadow-2xl mb-8 max-w-2xl mx-auto border border-blue-400 animate-slide-in-up">
                <h3 className="text-3xl font-bold text-white mb-4">{eventDetails.title}</h3>
                <p className="text-md text-blue-100 mb-6">{eventDetails.description}</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left mb-6 text-blue-100">
                    <p className="flex items-center"><span className="font-semibold text-blue-300 mr-2">📅 Date:</span> {eventDetails.displayDate}</p>
                    <p className="flex items-center"><span className="font-semibold text-blue-300 mr-2">⏰ Time:</span> {eventDetails.displayStartTime} - {eventDetails.displayEndTime}</p>
                    <p className="flex items-center col-span-2"><span className="font-semibold text-blue-300 mr-2">📍 Location:</span> {eventDetails.location || eventDetails.meetingLink || 'Virtual'}</p>
                </div>
                
                {inviterDetails && (
                    <p className="text-sm text-blue-200 italic mt-4 text-right">Invited by: {inviterDetails.username || inviterDetails.email} ({inviterDetails.role})</p>
                )}

                <div className="mt-8 pt-6 border-t border-blue-600">
                    <div className="text-center mb-6">
                        <button
                            onClick={() => setIsNewUserMode(!isNewUserMode)}
                            className="text-blue-300 text-base font-semibold hover:underline transition-colors duration-200"
                        >
                            {isNewUserMode ? 'Already have an account? Sign in here.' : 'New to Oratrics? Register for free!'}
                        </button>
                    </div>

                    {isNewUserMode && (
                        <div className="flex flex-col gap-4 mb-6">
                            <TextField
                                label="Full Name"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                fullWidth
                                variant="filled"
                                InputProps={{ className: 'rounded-lg bg-blue-800 text-white border border-blue-700 focus-within:border-blue-400', disableUnderline: true }}
                                InputLabelProps={{ className: 'text-blue-200' }}
                                sx={{ '& .MuiFilledInput-root': { borderRadius: '8px' } }}
                            />
                            <TextField
                                label="Email Address"
                                type="email"
                                value={userEmail}
                                onChange={(e) => setUserEmail(e.target.value)}
                                fullWidth
                                variant="filled"
                                InputProps={{ className: 'rounded-lg bg-blue-800 text-white border border-blue-700 focus-within:border-blue-400', disableUnderline: true }}
                                InputLabelProps={{ className: 'text-blue-200' }}
                                sx={{ '& .MuiFilledInput-root': { borderRadius: '8px' } }}
                            />
                            <TextField
                                label="Create Password"
                                type="password"
                                value={userPassword}
                                onChange={(e) => setUserPassword(e.target.value)}
                                fullWidth
                                variant="filled"
                                InputProps={{ className: 'rounded-lg bg-blue-800 text-white border border-blue-700 focus-within:border-blue-400', disableUnderline: true }}
                                InputLabelProps={{ className: 'text-blue-200' }}
                                sx={{ '& .MuiFilledInput-root': { borderRadius: '8px' } }}
                            />
                        </div>
                    )}

                    <button
                        onClick={handleAcceptInvitation}
                        className="w-full bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600 text-white font-bold py-3 px-8 rounded-full shadow-lg transform hover:scale-105 transition duration-300 ease-in-out focus:outline-none focus:ring-4 focus:ring-green-300 flex items-center justify-center"
                        disabled={acceptLoading}
                    >
                        {acceptLoading ? (
                            <>
                                <svg className="animate-spin h-5 w-5 mr-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Accepting...
                            </>
                        ) : (
                            <>
                                <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                                Accept Invitation
                            </>
                        )}
                    </button>
                    {acceptMessage && (
                        <p className={`mt-4 text-lg font-semibold flex items-center justify-center ${acceptSeverity === 'success' ? 'text-green-300' : 'text-red-400'}`}>
                            {acceptSeverity === 'success' ? (
                                <svg className="h-6 w-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                            ) : (
                                <svg className="h-6 w-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                            )}
                            {acceptMessage}
                        </p>
                    )}
                </div>
            </div>
        )}

        {/* Display error messages for invalid/expired/cancelled invitations */}
        {invitationCode && (invitationStatus === 'invalid' || invitationStatus === 'expired' || invitationStatus === 'cancelled' || invitationStatus === 'error') && (
            <div className="bg-red-700 bg-opacity-70 p-8 rounded-2xl shadow-2xl mb-8 max-w-lg mx-auto border border-red-500 animate-slide-in-up">
                <h3 className="text-2xl font-bold text-white mb-3 flex items-center justify-center">
                    <svg className="h-7 w-7 mr-3 text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                    Invitation {invitationStatus === 'expired' ? 'Expired' : invitationStatus === 'cancelled' ? 'Cancelled' : 'Invalid'}
                </h3>
                <p className="text-lg text-red-100">
                    {acceptMessage || "Oops! This invitation link is no longer valid or has expired. Please verify the link or contact your friend who invited you for assistance."}
                </p>
            </div>
        )}

        {/* Display accepted invitation state */}
        {invitationCode && invitationStatus === 'accepted' && eventDetails && (
            <div className="bg-green-700 bg-opacity-70 p-8 rounded-2xl shadow-2xl mb-8 max-w-2xl mx-auto border border-green-500 animate-slide-in-up">
                <h3 className="text-2xl font-bold text-white mb-3 flex items-center justify-center">
                    <svg className="h-7 w-7 mr-3 text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                    Invitation Accepted! Welcome!
                </h3>
                <p className="text-lg text-green-100 mb-6">
                    You've successfully accepted the invitation to: <strong className="text-white">{eventDetails.title}</strong>.
                    It will take place on {eventDetails.displayDate} from {eventDetails.displayStartTime} to {eventDetails.displayEndTime} ({eventDetails.displayTimezone}).
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-4 mt-6">
                    {eventDetails.meetingLink && (
                      <a
                        href={eventDetails.meetingLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-full shadow-md transform hover:scale-105 transition duration-300"
                      >
                        <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197 3.197m0 0l-3.197-3.197M11.554 14.365V4.635M17.5 12a5.5 5.5 0 11-11 0 5.5 5.5 0 0111 0z"></path></svg>
                        Go to Class
                      </a>
                    )}
                     <button
                        onClick={() => console.log('Redirect to dashboard')}
                        className="inline-flex items-center justify-center bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-6 rounded-full shadow-md transform hover:scale-105 transition duration-300"
                      >
                        <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m0 0l-7 7m7-7v10a1 1 0 00-1 1h-3M8 12h8"></path></svg>
                        My Dashboard
                      </button>
                </div>
            </div>
        )}

        {!invitationCode && (
            <>
                <div className="text-3xl md:text-4xl font-extrabold text-yellow-300 mb-8 relative z-10 animate-pulse-light drop-shadow-lg">
                    {countdownText}
                </div>
                <div className="flex flex-col md:flex-row justify-center gap-6 relative z-10">
                    <button
                        onClick={() => showModal('Joining functionality coming soon! Stay tuned!')}
                        className="bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600 text-white font-bold py-4 px-12 rounded-full shadow-xl transform hover:scale-105 transition duration-300 ease-in-out focus:outline-none focus:ring-4 focus:ring-green-300 flex items-center justify-center text-lg"
                    >
                        <svg className="h-6 w-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197 3.197m0 0l-3.197-3.197M11.554 14.365V4.635M17.5 12a5.5 5.5 0 11-11 0 5.5 5.5 0 0111 0z"></path></svg>
                        Join Activity Class
                    </button>
                    <button
                        onClick={handleBookDemo}
                        className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold py-4 px-12 rounded-full shadow-xl transform hover:scale-105 transition duration-300 ease-in-out focus:outline-none focus:ring-4 focus:ring-purple-300 flex items-center justify-center text-lg"
                    >
                        <svg className="h-6 w-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                        Book a Free Demo
                    </button>
                </div>
            </>
        )}
    </div>

    {/* Custom CSS for animations and patterns */}
    <style jsx>{`
        .bg-pattern-dots {
            background-image: radial-gradient(#ffffff33 1px, transparent 1px);
            background-size: 20px 20px;
        }

        @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in { animation: fadeIn 0.8s ease-out forwards; }
        .animate-fade-in.delay-200 { animation-delay: 0.2s; }

        @keyframes scaleIn {
            from { opacity: 0; transform: scale(0.9); }
            to { opacity: 1; transform: scale(1); }
        }
        .animate-scale-in { animation: scaleIn 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards; }

        @keyframes pulseFade {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
        }
        .animate-pulse-fade { animation: pulseFade 1.5s infinite ease-in-out; }

        @keyframes slideInUp {
            from { opacity: 0; transform: translateY(50px); }
            to { opacity: 1; transform: translateY(0); }
        }
        .animate-slide-in-up { animation: slideInUp 0.7s ease-out forwards; }

        @keyframes pulseLight {
            0%, 100% { opacity: 1; text-shadow: 0 0 8px rgba(255,255,0,0.7); }
            50% { opacity: 0.9; text-shadow: 0 0 15px rgba(255,255,0,0.9); }
        }
        .animate-pulse-light { animation: pulseLight 2s infinite ease-in-out; }
    `}</style>
</section>

      {/* The rest of your component remains the same */}
      {/* Platform Details Section */}
      <section className="py-16 px-4 md:px-8 bg-white rounded-xl mx-4 md:mx-8 mt-8 shadow-lg">
        <h3 className="text-4xl font-bold text-center mb-10 text-indigo-700">Why Oratrics Learning Solution?</h3>
        <div className="grid md:grid-cols-3 gap-8 text-center">
          <div className="p-6 bg-blue-50 rounded-xl shadow-md transform hover:scale-105 transition duration-300 border border-blue-200">
            <div className="text-5xl mb-4 text-violet-600">💡</div> <h4 className="text-2xl font-semibold mb-2 text-gray-800">Innovative Learning</h4>
            <p className="text-gray-600">Engage with cutting-edge curriculum and interactive lessons designed for the modern learner.</p>
          </div>
          <div className="p-6 bg-green-50 rounded-xl shadow-md transform hover:scale-105 transition duration-300 border border-green-200">
            <div className="text-5xl mb-4 text-emerald-600">🤝</div> <h4 className="text-2xl font-semibold mb-2 text-gray-800">Global Community</h4>
            <p className="text-gray-600">Connect with students and teachers from around the world in a vibrant learning ecosystem.</p>
          </div>
          <div className="p-6 bg-yellow-50 rounded-xl shadow-md transform hover:scale-105 transition duration-300 border border-yellow-200">
            <div className="text-5xl mb-4 text-orange-600">🌟</div> <h4 className="text-2xl font-semibold mb-2 text-gray-800">Expert Instructors</h4>
            <p className="text-gray-600">Learn from passionate educators who bring real-world experience to every class.</p>
          </div>
        </div>
        <div className="text-center mt-10">
          <button
            onClick={togglePlatformDetails}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-8 rounded-full shadow-md transition duration-300 focus:outline-none focus:ring-4 focus:ring-indigo-300"
          >
            {showPlatformDetails ? 'Hide More Details' : 'Discover More'}
          </button>
        </div>
        {showPlatformDetails && (
          <div className="mt-8 text-center max-w-4xl mx-auto text-gray-700">
            <p className="mb-4">
              At Oratrics Learning Solution, we believe in making education accessible, engaging, and enjoyable. Our platform offers a diverse range of courses, from STEM to arts, all delivered through an intuitive and interactive interface. We leverage the latest technology to provide personalized learning paths and foster a collaborative environment where every student can thrive.
            </p>
            <p>
              Our unique approach includes gamified learning modules, virtual labs, and real-time project collaboration, ensuring that learning is not just about acquiring knowledge, but also about developing critical thinking and problem-solving skills for the future.
            </p>
          </div>
        )}
      </section>

      {/* Open Activity Classes Section */}
      <section className="py-16 px-4 md:px-8 text-center bg-blue-50 rounded-xl mx-4 md:mx-8 mt-8 shadow-lg">
        <h3 className="text-4xl font-bold mb-6 text-fuchsia-700">Weekly Open Activity Classes</h3>
        <p className="text-xl mb-8 max-w-3xl mx-auto text-gray-700">
          Every week, our amazing teachers host special activity classes that are open to everyone – no enrollment required! It's a fantastic way to explore new interests, meet our community, and experience the Oratrics Learning Solution difference.
        </p>
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          <div className="p-6 bg-white rounded-xl shadow-md transform hover:scale-105 transition duration-300 border border-blue-200">
            <h4 className="text-2xl font-semibold mb-2 text-blue-600">Creative Coding Workshops</h4>
            <p className="text-gray-600">Dive into the world of programming with fun, hands-on projects.</p>
          </div>
          <div className="p-6 bg-white rounded-xl shadow-md transform hover:scale-105 transition duration-300 border border-green-200">
            <h4 className="text-2xl font-semibold mb-2 text-emerald-600">Virtual Science Experiments</h4>
            <p className="text-gray-600">Witness mind-blowing experiments and understand scientific principles.</p>
          </div>
          <div className="p-6 bg-white rounded-xl shadow-md transform hover:scale-105 transition duration-300 border border-purple-200">
            <h4 className="text-2xl font-semibold mb-2 text-violet-600">Digital Art & Design</h4>
            <p className="text-gray-600">Unleash your inner artist with modern digital tools and techniques.</p>
          </div>
          <div className="p-6 bg-white rounded-xl shadow-md transform hover:scale-105 transition duration-300 border border-yellow-200">
            <h4 className="text-2xl font-semibold mb-2 text-orange-600">Interactive Storytelling</h4>
            <p className="text-gray-600">Craft compelling narratives and explore different genres.</p>
          </div>
        </div>

        <div className="mt-12 max-w-3xl mx-auto text-gray-700">
          <h4 className="text-3xl font-bold mb-4 text-pink-600">What to Expect in an Activity Class:</h4>
          <ul className="list-disc list-inside text-left space-y-2">
            <li>Interactive, hands-on sessions led by expert instructors.</li>
            <li>Engaging discussions and collaborative problem-solving.</li>
            <li>Opportunities to ask questions and get personalized feedback.</li>
            <li>A chance to meet fellow learners from diverse backgrounds.</li>
            <li>Absolutely no prerequisites – just bring your curiosity!</li>
          </ul>
        </div>
      </section>

      {/* Engaging Game Section */}
      <section className="py-16 px-4 md:px-8 bg-white rounded-xl mx-4 md:mx-8 mt-8 shadow-lg">
        <h3 className="text-4xl font-bold text-center mb-6 text-indigo-700">Ready for a Challenge?</h3>
        <p className="text-xl text-center mb-8 max-w-3xl mx-auto text-gray-700">
          Test your reflexes with our quick "Catch the EduStar" game! A glimpse into the interactive fun awaiting you at Oratrics Learning Solution.
        </p>
        <div className="text-center mb-6">
          <button
            onClick={() => setShowGame(!showGame)}
            className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-8 rounded-full shadow-md transition duration-300 focus:outline-none focus:ring-4 focus:ring-purple-300"
          >
            {showGame ? 'Hide Game' : 'Play a Quick Game!'}
          </button>
        </div>

        {showGame && (
          <div className="relative w-full max-w-3xl h-80 bg-gray-100 rounded-xl mx-auto overflow-hidden border-2 border-indigo-300 shadow-inner">
            <div ref={gameAreaRef} className="absolute inset-0 flex items-center justify-center">
              {!gameActive ? (
                <button
                  onClick={startGame}
                  className="bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-8 rounded-full shadow-lg transition duration-300 focus:outline-none focus:ring-4 focus:ring-green-300"
                >
                  Start Game
                </button>
              ) : (
                <>
                  <div className="absolute top-4 left-4 text-xl font-bold text-gray-800">Score: {score}</div>
                  <div
                    className="game-star text-5xl cursor-pointer transform hover:scale-110 absolute"
                    style={{ left: starPosition.x, top: starPosition.y }}
                    onClick={handleStarClick}
                  >
                    ✨
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </section>

      {/* Temporary Dashboard Section */}
      <section className="py-16 px-4 md:px-8 bg-blue-50 rounded-xl mx-4 md:mx-8 mt-8 shadow-lg">
        <h3 className="text-4xl font-bold text-center mb-10 text-orange-600">Your Exclusive Sneak Peek!</h3>
        <p className="text-xl text-center mb-8 max-w-3xl mx-auto text-gray-700">
          As an invited guest, enjoy a temporary glimpse into your personalized Oratrics Learning Solution dashboard!
        </p>
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          <div className="p-6 bg-white rounded-xl shadow-md border border-green-300">
            <h4 className="text-2xl font-semibold mb-4 text-emerald-600">Daily Insight</h4>
            <p className="text-lg italic text-gray-600">
              "Consistency is key to mastery. Dedicate a small amount of time daily to see big results!"
            </p>
            <div className="mt-4 text-gray-500 text-sm">
              <p>Next recommended activity: "Introduction to AI Art"</p>
            </div>
          </div>
          <div className="p-6 bg-white rounded-xl shadow-md border border-red-300">
            <h4 className="text-2xl font-semibold mb-4 text-red-600">Brain Teaser Challenge</h4>
            <p className="text-lg mb-4 text-gray-600">What has an eye, but cannot see?</p>
            <input
              type="text"
              placeholder="Your answer..."
              value={challengeAnswer}
              onChange={(e) => setChallengeAnswer(e.target.value)}
              className="w-full p-2 rounded-md bg-gray-100 text-gray-800 border border-gray-300 focus:outline-none focus:border-blue-500"
            />
            <button
              onClick={handleSubmitChallenge}
              className="mt-4 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-full transition duration-300"
            >
              Submit
            </button>
            <p className={`mt-2 text-lg font-semibold ${challengeFeedback.includes('Correct') ? 'text-emerald-600' : 'text-red-600'}`}>{challengeFeedback}</p>
          </div>
          <div className="p-6 bg-white rounded-xl shadow-md border border-purple-300">
            <h4 className="text-2xl font-semibold mb-4 text-violet-600">AI-Powered Question</h4>
            <p className="text-lg italic text-gray-600 mb-4">{aiQuestion}</p>
            <button
              onClick={generateAIQuestion}
              className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-6 rounded-full shadow-md transition duration-300 focus:outline-none focus:ring-4 focus:ring-purple-300"
            >
              Generate New Question
            </button>
          </div>
          <div className="p-6 bg-white rounded-xl shadow-md border border-yellow-300">
            <h4 className="text-2xl font-semibold mb-4 text-orange-600">Your Learning Journey</h4>
            <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
              <div className="bg-violet-500 h-4 rounded-full" style={{ width: '65%' }}></div>
            </div>
            <p className="text-gray-600">You've completed 65% of your introductory learning path!</p>
            <p className="text-sm text-gray-500 mt-2">Unlock your full progress tracker by joining Oratrics Learning Solution!</p>
          </div>
        </div>
        <div className="text-center mt-10">
          <button
            onClick={handleUnlockDashboard}
            className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-bold py-4 px-10 rounded-full shadow-lg transform hover:scale-105 transition duration-300 ease-in-out focus:outline-none focus:ring-4 focus:ring-emerald-300"
          >
            Unlock Full Dashboard
          </button>
        </div>
      </section>

      {/* Meet the Instructor Section */}
      <section className="py-16 px-4 md:px-8 text-center bg-gradient-to-br from-purple-100 to-fuchsia-100 rounded-xl mx-4 md:mx-8 mt-8 shadow-lg">
        <h3 className="text-4xl font-bold mb-10 text-violet-700">Meet Your Future Instructor!</h3>
        <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8 border border-purple-300">
          <img src="https://placehold.co/150x150/A78BFA/FFFFFF?text=Instructor" alt="Instructor Profile" className="w-32 h-32 rounded-full mx-auto mb-6 border-4 border-violet-500"/>
          <h4 className="text-3xl font-bold text-gray-800 mb-2">Ms. Anya Sharma</h4>
          <p className="text-lg text-gray-600 mb-4">Lead Creative Coding Instructor</p>
          <p className="text-gray-700 leading-relaxed">
            Ms. Anya brings over 10 years of experience in software development and a passion for teaching kids. She believes that coding is a superpower that unlocks creativity and problem-solving skills. Join her next activity class and discover the magic of building your own digital worlds!
          </p>
          <button
            onClick={() => showModal('Instructor profile details coming soon!')}
            className="mt-8 bg-gradient-to-r from-violet-600 to-indigo-700 hover:from-violet-700 hover:to-indigo-800 text-white font-bold py-3 px-8 rounded-full shadow-md transition duration-300 focus:outline-none focus:ring-4 focus:ring-violet-300"
          >
            View Full Profile
          </button>
        </div>
      </section>

      {/* Success Stories / Impact Section */}
      <section className="py-16 px-4 md:px-8 text-center bg-white rounded-xl mx-4 md:mx-8 mt-8 shadow-lg">
        <h3 className="text-4xl font-bold mb-10 text-blue-700">Oratrics in Numbers</h3>
        <p className="text-xl mb-8 max-w-3xl mx-auto text-gray-700">
          See the real impact Oratrics Learning Solution is making in students' lives worldwide.
        </p>
        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          <div className="p-6 bg-blue-50 rounded-xl shadow-md border border-blue-200">
            <div className="text-6xl font-extrabold text-blue-600 mb-2">10K+</div>
            <p className="text-lg font-semibold text-gray-700">Happy Students Enrolled</p>
          </div>
          <div className="p-6 bg-green-50 rounded-xl shadow-md border border-green-200">
            <div className="text-6xl font-extrabold text-emerald-600 mb-2">500+</div>
            <p className="text-lg font-semibold text-gray-700">Engaging Activity Classes</p>
          </div>
          <div className="p-6 bg-yellow-50 rounded-xl shadow-md border border-yellow-200">
            <div className="text-6xl font-extrabold text-orange-600 mb-2">95%</div>
            <p className="text-lg font-semibold text-gray-700">Student Satisfaction Rate</p>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 px-4 md:px-8 text-center bg-blue-50 rounded-xl mx-4 md:mx-8 mt-8 shadow-lg">
        <h3 className="text-4xl font-bold mb-10 text-emerald-600">What Our Students Say</h3>
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          <div className="p-6 bg-white rounded-xl shadow-md border border-purple-200">
            <p className="text-lg italic mb-4 text-gray-700">"Oratrics Learning Solution transformed my understanding of coding. The activity classes are incredibly fun and insightful!"</p>
            <p className="font-semibold text-violet-600">- Alex R., Aspiring Developer</p>
          </div>
          <div className="p-6 bg-white rounded-xl shadow-md border border-blue-200">
            <p className="text-lg italic mb-4 text-gray-700">"I never thought science could be this engaging. The virtual experiments are mind-blowing!"</p>
            <p className="font-semibold text-blue-600">- Maya S., Science Enthusiast</p>
          </div>
          <div className="p-6 bg-white rounded-xl shadow-md border border-yellow-200">
            <p className="text-lg italic mb-4 text-gray-700">"The instructors are truly passionate. I've learned so much about digital art in just a few weeks."</p>
            <p className="font-semibold text-orange-600">- Chris L., Digital Artist</p>
          </div>
          <div className="p-6 bg-white rounded-xl shadow-md border border-pink-200">
            <p className="text-lg italic mb-4 text-gray-700">"A fantastic community and a great way to learn new skills. Highly recommend the storytelling classes!"</p>
            <p className="font-semibold text-fuchsia-600">- Sarah K., Creative Writer</p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 md:px-8 bg-white rounded-xl mx-4 md:mx-8 mt-8 shadow-lg">
        <h3 className="text-4xl font-bold text-center mb-10 text-indigo-700">Frequently Asked Questions</h3>
        <div className="max-w-3xl mx-auto bg-blue-50 rounded-xl shadow-md border border-blue-200">
          <div className="faq-item">
            <div className="faq-question" onClick={toggleFaq}>
              <span>What are Open Activity Classes?</span>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
            </div>
            <div className="faq-answer">
              <p>These are weekly, free-to-join classes hosted by our expert teachers, covering various exciting topics like coding, science, art, and storytelling. They are open to everyone, whether you're an enrolled student or just curious!</p>
            </div>
          </div>
          <div className="faq-item">
            <div className="faq-question" onClick={toggleFaq}>
              <span>Do I need to register for activity classes?</span>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
            </div>
            <div className="faq-answer">
              <p>No, you don't! Our Open Activity Classes are designed to be easily accessible. Just click the "Join Activity Class Now!" button when the class is live, or keep an eye on the countdown timer for the next session.</p>
            </div>
          </div>
          <div className="faq-item">
            <div className="faq-question" onClick={toggleFaq}>
              <span>What makes Oratrics Learning Solution unique?</span>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
            </div>
            <div className="faq-answer">
              <p>We blend innovative curriculum with interactive learning tools, a global community, and passionate instructors. Our focus is on hands-on experience, critical thinking, and making learning genuinely fun and engaging for all ages.</p>
            </div>
          </div>
          <div className="faq-item">
            <div className="faq-question" onClick={toggleFaq}>
              <span>Is there a cost to join the activity classes?</span>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
            </div>
            <div className="faq-answer">
              <p>No, the Weekly Open Activity Classes are completely free! They are our way of giving back to the community and showcasing the dynamic learning environment at Oratrics Learning Solution.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="text-center py-16 px-4 md:px-8 bg-gradient-to-r from-blue-600 to-purple-700 rounded-xl mx-4 md:mx-8 mt-8 shadow-lg">
        <h3 className="text-4xl font-bold mb-6 text-white">Ready to Start Your Learning Adventure?</h3>
        <p className="text-xl mb-8 max-w-3xl mx-auto text-blue-100">
          Join the Oratrics Learning Solution community today and unlock a world of knowledge, creativity, and endless possibilities.
          Your future starts here.
        </p>
       <a href="https://demo.oratrics.com"
          className="bg-white text-purple-700 font-bold py-4 px-10 rounded-full shadow-lg transform hover:scale-105 transition duration-300 ease-in-out focus:outline-none focus:ring-4 focus:ring-white"
        >
          Explore Courses & Sign Up
        </a>
      </section>

      {/* Footer Section */}
      <footer className="py-8 px-4 md:px-8 text-center bg-gray-800 text-gray-400 text-sm mt-8 rounded-t-xl">
        <p>&copy; {new Date().getFullYear()} Oratrics Learning Solution. All rights reserved.</p>
        <div className="flex justify-center space-x-4 mt-4">
          <a href="#" className="hover:text-white transition duration-300">Privacy Policy</a>
          <a href="#" className="hover:text-white transition duration-300">Terms of Service</a>
        </div>
      </footer>
    </div>
  );
};

export default InvitationPage;